import React, { useEffect, useRef, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { Box, capitalize, CircularProgress, MenuItem, Select, Skeleton, Stack } from '@mui/material'

import { useStores } from '@trivie/core'
import { DateFilter } from '@trivie/ui-web/components/date-filter'
import { Header } from '@trivie/ui-web/components/header'
import { Leaderboard } from '@trivie/ui-web/components/leaderboard'
import { GenerateReport } from '@trivie/ui-web/components/generate-report'
import { ArrowDropDownRounded } from '@mui/icons-material'
import { color } from '@trivie/ui-web/theme'
import { Text } from '@trivie/ui-web/components/text'
import { DateTime } from 'luxon'
import { ReportsApi } from '@trivie/core/src/services/api-objects'

export const LeaderboardsScreen: React.FunctionComponent = observer(() => {
  const { manageLeaderboardStore, manageUserStore } = useStores()
  const { userProfile } = manageUserStore
  const { getLeaderboardBase, userLeaderboard, groupLeaderboard } = manageLeaderboardStore

  const gdpr_enabled = userProfile?.workspace_settings?.gdpr_enabled

  const [start, setStart] = useState<any>(DateTime.local(1971).startOf('day').toISO())
  const [end, setEnd] = useState<any>(DateTime.local().endOf('day').toISO())
  const [userStatus, setUserStatus] = useState('all')

  const [loadingUserLeaderboard, setLoadingUserLeaderboard] = useState(true)
  const [loadingGroupLeaderboard, setLoadingGroupLeaderboard] = useState(true)

  const groupLeaderboardRef = useRef<any>()
  const userLeaderboardRef = useRef<any>()
  useEffect(() => {
    return () => {
      cancelRequests()
    }
  }, [])

  const cancelRequests = () => {
    if (userLeaderboardRef.current) {
      userLeaderboardRef.current.cancel()
    }
    if (groupLeaderboardRef.current) {
      groupLeaderboardRef.current.cancel()
    }
  }

  const load = (startDateTime, endDateTime, status) => {
    cancelRequests()

    setLoadingUserLeaderboard(true)
    setLoadingGroupLeaderboard(true)

    const userLeaderboardCancel = ReportsApi.getCancelToken()
    userLeaderboardRef.current = userLeaderboardCancel
    getLeaderboardBase(startDateTime, endDateTime, false, status, userLeaderboardCancel.token)
      .then(() => setLoadingUserLeaderboard(false))
      .catch(() => setLoadingUserLeaderboard(true))

    const groupLeaderboardCancel = ReportsApi.getCancelToken()
    groupLeaderboardRef.current = groupLeaderboardCancel
    getLeaderboardBase(startDateTime, endDateTime, true, status, groupLeaderboardCancel.token)
      .then(() => setLoadingGroupLeaderboard(false))
      .catch(() => setLoadingGroupLeaderboard(true))
  }

  const onDateChange = (startDateTime, endDateTime) => {
    setStart(startDateTime)
    setEnd(endDateTime)
    load(startDateTime, endDateTime, userStatus)
  }

  const onUserStatusChange = (e) => {
    setUserStatus(e.target.value)
    load(start, end, e.target.value)
  }

  return (
    <>
      <Header divider title="Leaderboards" />
      <Stack mb={2} direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction="row" gap={1}>
          <DateFilter onDateChange={onDateChange} />
          <Select
            sx={{ '& .MuiSelect-select:focus': { backgroundColor: 'transparent' } }}
            autoWidth
            renderValue={(value) => (
              <Text
                variant="labelMedium"
                text={`${capitalize(value)} Users`}
                style={{ color: color.shade70 }}
              />
            )}
            disableUnderline
            variant="standard"
            value={userStatus}
            IconComponent={ArrowDropDownRounded}
            onChange={onUserStatusChange}
          >
            <MenuItem value={'all'}>All Users</MenuItem>
            <MenuItem value={'active'}>Active Users</MenuItem>
            <MenuItem value={'deactivated'}>Deactivated Users</MenuItem>
          </Select>
        </Stack>
        <GenerateReport filename="leaderboard" orientation="portrait" />
      </Stack>
      <Stack useFlexGap gap={2} direction="row" flexWrap="wrap" pb="40px">
        {!gdpr_enabled &&
          (loadingUserLeaderboard ? (
            <Box flex={1} minHeight={500} sx={{ width: '100%' }}>
              <Skeleton sx={{ transform: 'none', height: '100%', borderRadius: '20px' }} />
            </Box>
          ) : (
            <Leaderboard type="user" data={userLeaderboard} />
          ))}
        {loadingGroupLeaderboard ? (
          <Box flex={1} minHeight={500} sx={{ width: '100%' }}>
            <Skeleton sx={{ transform: 'none', height: '100%', borderRadius: '20px' }} />
          </Box>
        ) : (
          <Leaderboard type="group" data={groupLeaderboard} />
        )}
      </Stack>
    </>
  )
})
